import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext, CartAppContext } from '../../App';
import ShopItemComponent from './ShopItemComponent';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { requestQuotesFromCatalog } from '../../clients/quote_client';
import {
  getOrderContactSettings,
} from '../../clients/order_request_client';
import { showToast, clearPersistedCart } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import GlobalOrderContactModal from '../OrderRequest/GlobalOrderContactModal';

const LOG_CONTEXT = 'CartModal';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

/**
 * Cart modal: review items and send supplier quote-request emails (no Quote records).
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {function} props.setShow
 */
const CartModal = ({ show, setShow }) => {
  const { token } = useContext(AppContext);
  const { cart, setCart } = useContext(CartAppContext);

  const [groupedCart, setGroupedCart] = useState([]);
  const [step, setStep] = useState('cart');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [successResult, setSuccessResult] = useState(null);
  const [showGlobalContactModal, setShowGlobalContactModal] = useState(false);
  const [globalContactSettings, setGlobalContactSettings] = useState(null);
  const [isLoadingGlobalContact, setIsLoadingGlobalContact] = useState(false);
  const [globalContactLoadError, setGlobalContactLoadError] = useState('');

  useEffect(() => {
    if (!Array.isArray(cart)) {
      setGroupedCart({});
      return;
    }
    const groupedBySupplier = cart.reduce((acc, item) => {
      const supplierKey = item?.supplier?.id;
      if (!acc[supplierKey]) {
        acc[supplierKey] = [];
      }
      acc[supplierKey].push(item);
      return acc;
    }, {});
    setGroupedCart(groupedBySupplier);
  }, [cart]);

  useEffect(() => {
    if (!show) {
      setStep('cart');
      setSubmitErrors([]);
      setSuccessResult(null);
      setIsSubmitting(false);
      setShowGlobalContactModal(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !token) return;
    const loadGlobalSettings = async () => {
      setIsLoadingGlobalContact(true);
      setGlobalContactLoadError('');
      const response = await getOrderContactSettings(token);
      setIsLoadingGlobalContact(false);
      if (response?.success) {
        setGlobalContactSettings(response.data);
      } else {
        setGlobalContactSettings(null);
        setGlobalContactLoadError(
          response?.messages?.[0] ||
            'Lab contact settings could not be loaded.'
        );
      }
    };
    loadGlobalSettings();
  }, [show, token]);

  const uniqueOrderRequestIds = [
    ...new Set(
      (cart || [])
        .map(row => row?.order_request_id)
        .filter(id => Number.isInteger(id) || (typeof id === 'string' && id))
    ),
  ];
  const hasSingleOrderRequestContext = uniqueOrderRequestIds.length === 1;
  const hasAllocatedGlobalContact =
    !!globalContactSettings?.contact_name && !!globalContactSettings?.contact_email;
  let sendDisabledReason = '';
  if (isLoadingGlobalContact) {
    sendDisabledReason = 'Loading lab contact settings...';
  } else if (globalContactLoadError) {
    sendDisabledReason =
      'Quote requests are disabled because lab contact settings could not be loaded.';
  } else if (!hasAllocatedGlobalContact) {
    sendDisabledReason =
      'Quote requests are disabled until a lab contact is configured.';
  }
  const isSendDisabled =
    Object.keys(groupedCart).length === 0 || !!sendDisabledReason || isSubmitting;

  const handleClose = () => {
    if (step === 'success') {
      clearPersistedCart();
    }
    setShow(false);
  };

  /**
   * Clears cart state after a successful quote request. Unmounts line items first, then
   * relies on App.js persistCart effect to write [] to storage (avoids racing clearPersistedCart).
   */
  const onSuccessfulSend = () => {
    const cartLengthBefore = cart?.length ?? 0;
    logInfo(LOG_CONTEXT, 'clearCartAfterQuoteSuccess', {
      cartLengthBefore,
    });
    setStep('success');
    setGroupedCart({});
    setCart([]);
    logInfo(LOG_CONTEXT, 'clearCartAfterQuoteSuccess scheduled', {
      cartLengthBefore,
      cartLengthAfter: 0,
    });
  };

  const updateGroupedItem = useCallback(
    (supplierKey, index, field, value) => {
      setGroupedCart(prev => {
        const updatedGroup = { ...prev };
        if (!updatedGroup[supplierKey]?.[index]) {
          return prev;
        }
        updatedGroup[supplierKey] = [...updatedGroup[supplierKey]];
        updatedGroup[supplierKey][index] = {
          ...updatedGroup[supplierKey][index],
          [field]: value,
        };
        setCart(Object.values(updatedGroup).flat());
        return updatedGroup;
      });
    },
    [setCart]
  );

  const removeGroupedItem = useCallback(
    (e, supplierKey, index) => {
      e.preventDefault();
      setGroupedCart(prev => {
        const updatedGroup = { ...prev };
        if (!updatedGroup[supplierKey]) {
          return prev;
        }
        updatedGroup[supplierKey] = [...updatedGroup[supplierKey]];
        updatedGroup[supplierKey].splice(index, 1);
        if (updatedGroup[supplierKey].length === 0) {
          delete updatedGroup[supplierKey];
        }
        setCart(Object.values(updatedGroup).flat());
        return updatedGroup;
      });
    },
    [setCart]
  );

  const buildValidLines = sourceRows =>
    (Array.isArray(sourceRows) ? sourceRows : [])
      .map(row => ({
        product_id: Number(row?.product),
        quantity: Number(row?.quantity),
      }))
      .filter(
        line =>
          Number.isInteger(line.product_id) &&
          line.product_id > 0 &&
          Number.isInteger(line.quantity) &&
          line.quantity > 0
      );

  const handleSendQuoteRequests = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitErrors([]);

    const lines = buildValidLines(cart);
    if (lines.length === 0) {
      setSubmitErrors([
        'Cart has no valid lines. Add at least one product with quantity >= 1.',
      ]);
      setIsSubmitting(false);
      return;
    }
    const payload = { lines };
    if (hasSingleOrderRequestContext) {
      payload.order_request_id = Number(uniqueOrderRequestIds[0]);
    }

    logInfo(LOG_CONTEXT, 'requestQuotesFromCatalog submit', {
      lineCount: payload.lines.length,
    });

    const response = await requestQuotesFromCatalog(token, payload);
    setIsSubmitting(false);

    if (response?.success) {
      setSuccessResult(response.data);
      onSuccessfulSend();
      showToast('Supplier quote request emails sent', 'success', 'top-right', 4000);
      logInfo(LOG_CONTEXT, 'catalog email batch success', {
        supplierCount: response.data?.suppliers?.length,
        corporateDemandRef: response.data?.corporate_demand_ref,
      });
    } else {
      setSubmitErrors(
        response?.messages?.length
          ? response.messages
          : ['Request failed. Please try again.']
      );
    }
  };

  return (
    <div>
      <Modal open={show} onClose={handleClose} aria-labelledby="product-modal">
        <Box sx={modalStyle}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3" component="h2">
              {step === 'success'
                ? 'Emails sent'
                : 'Materiah Cart'}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2, borderColor: '#424242' }} />

          {step === 'success' && successResult?.suppliers ? (
            <Box>
              {successResult.corporate_demand_ref ? (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporate demand ref:{' '}
                  <strong>{successResult.corporate_demand_ref}</strong>
                </Typography>
              ) : null}
              {successResult.suppliers.map((s, idx) => (
                <Box
                  key={s.supplier_id ?? s.supplier_name ?? idx}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h6">{s.supplier_name}</Typography>
                  <Typography variant="body2">
                    {s.item_count != null ? `${s.item_count} item(s) — ` : ''}
                    email {s.emailSent ? 'sent' : 'not sent'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : Object.keys(groupedCart).length > 0 ? (
            <>
              {submitErrors.length > 0 ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitErrors.map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </Alert>
              ) : null}
              {Object.keys(groupedCart).map(supplierKey => {
                const supplierName = groupedCart[supplierKey][0].supplier.name;
                return (
                  <Container key={supplierKey} sx={{ mb: 2 }}>
                    <Divider textAlign="left">
                      <Chip
                        label={supplierName}
                        sx={{ fontSize: '1.5rem', mb: 2 }}
                      />
                    </Divider>
                    {groupedCart[supplierKey].map((item, localIndex) => (
                      <ShopItemComponent
                        key={`${item.cat_num}-${localIndex}`}
                        supplierKey={supplierKey}
                        index={localIndex}
                        dividerStop={
                          groupedCart[supplierKey].length === localIndex + 1
                        }
                        grouplength={groupedCart[supplierKey].length}
                        item={item}
                        onItemChange={updateGroupedItem}
                        handleItemDelete={removeGroupedItem}
                      />
                    ))}
                  </Container>
                );
              })}
              <Divider sx={{ my: 2, borderColor: '#424242' }} />
            </>
          ) : (
            <Container>
              <Grid
                container
                sx={{ height: '100%', my: 15 }}
                justifyContent="center"
                alignItems="center"
              >
                <Grid item>
                  <Typography variant="h4" component="h2">
                    Cart&apos;s Empty!
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          )}

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <Grid item>
              {step === 'success' ? (
                <Button variant="contained" onClick={handleClose}>
                  Done
                </Button>
              ) : isSubmitting ? (
                <Button variant="contained" disabled>
                  <CircularProgress size={25} />
                </Button>
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title={sendDisabledReason || ''}>
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleSendQuoteRequests}
                        disabled={isSendDisabled}
                      >
                        Request Quote(s)
                      </Button>
                    </span>
                  </Tooltip>
                  {sendDisabledReason && (
                    <Link
                      component="button"
                      type="button"
                      underline="always"
                      onClick={() => setShowGlobalContactModal(true)}
                    >
                      Set lab contact
                    </Link>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item>
              {step !== 'success' ? (
                <Button
                  variant="contained"
                  style={{ backgroundColor: 'lightslategray', color: 'white' }}
                  onClick={handleClose}
                >
                  Close
                </Button>
              ) : null}
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <GlobalOrderContactModal
        open={showGlobalContactModal}
        onClose={() => setShowGlobalContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setGlobalContactSettings(settings);
          setGlobalContactLoadError('');
          showToast('Lab contact saved successfully', 'success', 'top-right', 3000);
        }}
      />
    </div>
  );
};
export default CartModal;
